/**
 * Feature flags for gating functionality that is not part of the current MVP.
 *
 * Principen: dölj funktioner bakom en flagga i stället för att radera koden,
 * så att de kan återaktiveras post-MVP utan att byggas om.
 */

/**
 * Dark mode / theme toggle.
 *
 * Funktionen är inte prioriterad för MVP och är inte genomgående verifierad i
 * alla komponenter. När flaggan är `false` döljs temaväxlaren och appen tvingas
 * alltid till light mode. Temalogik, CSS-variabler och sparade temavärden
 * (localStorage `app-skin-dark` samt backend-preferensen `theme`) lämnas orörda
 * och återanvänds när flaggan sätts till `true` igen.
 */
export const FEATURE_DARK_MODE: boolean = false;

/**
 * Genväg "Konflikthantering" i projekthuvudet.
 *
 * En konflikt gäller alltid ett konkret etappmål; en fristående knapp bredvid
 * "Tillbaka" lyfter åtgärden ur det sammanhanget ("konflikt om vad?"). Beslut
 * 2026-09-25: dölj den. Konflikt startas från etapp- och attesteringsflödet.
 * Knappen och etappväljaren finns kvar i ProjectView och kan återaktiveras när
 * eskaleringstrappan är fastställd.
 */
export const FEATURE_PROJECT_CONFLICT_SHORTCUT: boolean = false;

/**
 * Finansieringsetiketten "Invändning på etapp" / "Åtgärd pågår" ovanför
 * projektets flikar (ClickUp 869fh4ad1).
 *
 * Etiketten såg ut som en invändningsknapp men fällde bara ut
 * finansieringsrutan. En invändning gäller en etapp, inte finansieringen
 * (pengarna ligger kvar reserverade), och syns redan i projektstatusen och på
 * etappkortet, där den också hanteras. Beslut 2026-10-16: dölj den, som
 * konfliktgenvägen. Etiketten för riktiga finansieringsavvikelser (utbetalning
 * misslyckades, överskott) visas som tidigare. Koden finns kvar i
 * ProjectFundingPanel.
 */
export const FEATURE_FUNDING_OBJECTION_TAG: boolean = false;

/**
 * Ny kontraktswizard (ClickUp 869f855pb, design "B · Utan kontraktsteg").
 *
 * När flaggan är `true` ersätter den nya overlay-guiden i tre steg (Uppdrag &
 * filer · Pris & etappmål · Granska & signera) det gamla skapa-formuläret för
 * Anbud/Fast pris. Typvalet visas bara när ÄTA är möjligt (ett kontrakt är
 * signerat av båda parter); annars öppnar "Skapa kontrakt" guiden direkt. ÄTA
 * går vidare till det befintliga ÄTA-flödet. "Redigera" i kontraktslistan
 * öppnar fastpriskontrakt i guiden (utkast, skickat, signerat).
 *
 * Beslut 2026-09-28 (wizard 6/6): på i alla miljöer direkt (inga kunder ännu).
 * Flaggan och det gamla skapa-flödet tas bort i en städ-PR när guiden är
 * verifierad på staging.
 */
export const FEATURE_CONTRACT_WIZARD_V2: boolean = true;

/**
 * Kontraktsvyn som lägesvy (ClickUp 869fa5qf8).
 *
 * Kontraktsfliken visar läget, en sammanfattning i läsläge (belopp, etappmål,
 * bilagor) och historiken. De gamla sektionerna Etappmål (med "Skapa
 * etappmålsplan – Guidad process" och "Skapa etappmål"), Kontrakt, Filer till
 * kontraktet och Uppdragsbeskrivning för kontrakt döljs: skapande och
 * redigering sker i kontraktsguiden. När flaggan är `true` visas de igen.
 *
 * Kontrakt som guiden inte kan öppna (ÄTA och manuellt signerade) behåller de
 * gamla sektionerna för byggföretaget, eftersom de saknar annan väg att
 * redigeras.
 *
 * Beslut 2026-10-05: dolda i alla miljöer. Koden tas bort i en städ-PR när
 * lägesvyn är verifierad på staging.
 */
export const FEATURE_CONTRACT_VIEW_LEGACY_SECTIONS: boolean = false;

/**
 * Projekt utan beställare (ClickUp 869ff1egy).
 *
 * När flaggan är `true` kan hantverkaren skapa ett projekt utan beställare:
 * Skapa projekt får valen Välj befintlig / Bjud in ny / Lägg till senare, och
 * projektvyn visar "Klart för signering" och "Väntar på BankID". Utskick och
 * signering är alltid spärrade i API:t så länge projektet saknar beställare,
 * oavsett flaggan. När flaggan är `false` krävs en verifierad beställare som
 * tidigare.
 *
 * Beslut 2026-10-28: på i alla miljöer direkt (inga kunder i prod ännu).
 */
export const FEATURE_PROJECT_WITHOUT_CUSTOMER: boolean = true;
